import React, { useEffect, useState } from "react";
import {
  Container,
  Drawer,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  IconButton,
  Typography,
  Collapse,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import useLogout from "../../../hooks/auth/authHook.js";
import { ROUTES_CONSTANTS } from "../../../constants/routesConstants.js";
import styles from "./navigationDrawerStyles.js";
import { useSelector } from "react-redux";
import { ArchiveBox, Box1, Category, ClipboardText, Document, DocumentForward, Element4, I3DCubeScan, LogoutCurve, Moneys, People, Profile2User, ProfileCircle, Receipt1, Receipt21 } from "iconsax-react";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { usePermissions } from "../../../hooks/permissions/usePermissions.js";

const NavigationDrawer = ({
  isMobile,
  isMobileOpen,
  setIsMobileOpen,
}) => {
  const [logout] = useLogout();
  const { pathname } = useLocation();
  const [selectedRoute, setSelectedRoute] = useState();
  const { hasPermission } = usePermissions();
  const [packagesOpen, setPackagesOpen] = useState(false);

  const modulePermissions = {
    Dashboard: null,
    Clients: "cli_vw",
    Users: "usr_vw",
    Tracking: "trk_vw",
    Lots: "lot_vw",
    Guides: "rte_vw",
    DeliveryGuides: "dgu_vw",
    Deliverers: "cut_vw",
    Categories: "cat_vw",
    Report: "rep_vw",
    Tickets: "tkt_vw",
  };

  const hasUsersPermission = hasPermission(modulePermissions.Users) && 
    (hasPermission("usr_vw_d") || hasPermission("usr_vwg") || hasPermission("usr_vwa"));

  const hasCategoriesPermission = hasPermission(modulePermissions.Categories) && 
    hasPermission("cat_ovw");
  
  const hasTicketsPermission = hasPermission(modulePermissions.Tickets) && 
    hasPermission("tkt_ovw");
  
  const hasTrackPermission = hasPermission(modulePermissions.Tracking) && 
    hasPermission("trk_vwg");
  
  const hasDeliveryGuidesPermission = hasPermission(modulePermissions.DeliveryGuides) && 
    hasPermission("dgu_ovw");

  const hasAnyPackagePermission =
  hasPermission(modulePermissions.Tracking) ||
  hasPermission(modulePermissions.Lots) ||
  hasPermission(modulePermissions.Guides) ||
  hasPermission(modulePermissions.DeliveryGuides);

  const userInformation = useSelector(state => state.user.userInformation);
  const { extraInformation } = useSelector(state => state.user);

  const handleCloseDrawer = () => {
    setIsMobileOpen(false);
  };

  const handleClickDrawerItem = (route) => {
    setSelectedRoute(route);
    handleCloseDrawer();
  };

  const handlePackagesClick = () => {
    setPackagesOpen(!packagesOpen);
  };
  
  useEffect(() => {
    const routes = {
      '/panel': 'Dashboard',
      '/clientes': 'Clients',
      '/paquetes': 'Packages',
      '/usuarios': 'Users',
      '/recoleccion': 'Lots',
      '/rutas': 'Guides',
      '/repartidores': 'Deliverers',
      '/categorias': 'Categories',
      '/saldos': 'Balances',
      '/por-entregar': 'DeliveryGuides',
      '/reporte': 'Report',
      '/rastreo': 'Tracking',
      '/tickets': 'Tickets',
    }
    
    const currentRoute = Object.keys(routes).find(
      (route) => pathname === route || pathname.startsWith(route)
    );
    setSelectedRoute(routes[currentRoute]);
  }, [pathname]);

  const drawer = (
    <Container style={styles.mainContainer}>
      <Container style={styles.logoContainer}>
        <img
          src="https://assets-liber.s3.amazonaws.com/images/liber_logo.jpeg"
          style={styles.logoImage}
          alt="Logo de Liber"
        />
        <Typography variant="h3" style={styles.logoName}>
          LIBER
        </Typography>
      </Container>

      <Divider component="li" style={styles.divider} />

      <ListItemButton onClick={() => {}} >
        <ListItemIcon>
          <Avatar sx={styles.avatar}>
            <ProfileCircle variant="Outline"/>
          </Avatar>
        </ListItemIcon>
        <ListItemText primary={`${userInformation.user_name} ${userInformation.paternal_lastname} ${userInformation.maternal_lastname}`}/>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          style={styles.logoutButton}
          onClick={logout}
        >
          <LogoutCurve variant="Outline"/>
        </IconButton>
      </ListItemButton>

      {extraInformation?.id_role === 1 && (
        <Container style={styles.regionContainer}>
          <Typography variant="h3" style={styles.regionText}>
            {extraInformation?.zone_names && extraInformation.zone_names.length > 0
              ? `Región ${extraInformation.zone_names[0]}`
              : "Sin región"}
          </Typography>
        </Container>
      )}

      <Divider component="li" style={styles.divider} />

      <List>
        {/* Navigation Links */}
        <ListItemButton
          selected={selectedRoute === "Dashboard"}
          component={Link}
          to={ROUTES_CONSTANTS.DASHBOARD}
          style={selectedRoute === "Dashboard" ? styles.selectedItem : styles.unselectedItem}
          onClick={() => handleClickDrawerItem("Dashboard")}
        >
          <ListItemIcon sx={styles.itemIcon}>
            <Element4 variant={selectedRoute === "Dashboard" ? "Bold" : "Outline"} style={selectedRoute === "Dashboard" ? styles.selectedItemIcon : styles.unselectedItemIcon}/>
          </ListItemIcon>
          <ListItemText primary="Panel de control" />
        </ListItemButton>
        
        { hasPermission(modulePermissions.Clients) && (<ListItemButton
          selected={selectedRoute === "Clients"}
          component={Link}
          to={ROUTES_CONSTANTS.CLIENTS}
          style={selectedRoute === "Clients" ? styles.selectedItem : styles.unselectedItem}
          onClick={() => handleClickDrawerItem("Clients")}
        >
          <ListItemIcon sx={styles.itemIcon}>
            <People variant={selectedRoute === "Clients" ? "Bold" : "Outline"} style={selectedRoute === "Clients" ? styles.selectedItemIcon : styles.unselectedItemIcon}/>
          </ListItemIcon>
          <ListItemText primary="Clientes" />
        </ListItemButton>)}

        {hasUsersPermission && (<ListItemButton
          selected={selectedRoute === "Users"}
          component={Link}
          to={ROUTES_CONSTANTS.USERS}
          style={selectedRoute === "Users" ? styles.selectedItem : styles.unselectedItem}
          onClick={() => handleClickDrawerItem("Users")}
        >
          <ListItemIcon sx={styles.itemIcon}>
            <Profile2User variant={selectedRoute === "Users" ? "Bold" : "Outline"} style={selectedRoute === "Users" ? styles.selectedItemIcon : styles.unselectedItemIcon}/>
          </ListItemIcon>
          <ListItemText primary="Usuarios" />
        </ListItemButton>)}

        {hasAnyPackagePermission && (<ListItemButton
          selected={selectedRoute === "Packages" || selectedRoute === "Tracking" || selectedRoute === "Lots" || selectedRoute === "Guides" || selectedRoute === "DeliveryGuides"}
          onClick={handlePackagesClick}
          style={selectedRoute === "Packages" || selectedRoute === "Tracking" || selectedRoute === "Lots" || selectedRoute === "Guides" || selectedRoute === "DeliveryGuides" ? styles.selectedItem : styles.unselectedItem}
        >
          <ListItemIcon sx={styles.itemIcon}>
            <ArchiveBox variant={selectedRoute === "Packages" || selectedRoute === "Tracking" || selectedRoute === "Lots" || selectedRoute === "Guides" || selectedRoute === "DeliveryGuides" ? "Bold" : "Outline"} style={selectedRoute === "Packages" || selectedRoute === "Tracking" || selectedRoute === "Lots" || selectedRoute === "Guides" || selectedRoute === "DeliveryGuides" ? styles.selectedItemIcon : styles.unselectedItemIcon}/>
          </ListItemIcon>
          <ListItemText primary="Paquetes" />
          {packagesOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>)}
        {hasAnyPackagePermission && (<Collapse in={packagesOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {hasTrackPermission && (<ListItemButton
              selected={selectedRoute === "Tracking"}
              component={Link}
              to={ROUTES_CONSTANTS.TRACKING}
              style={selectedRoute === "Tracking" ? styles.selectedItem : styles.unselectedItem}
              onClick={() => handleClickDrawerItem("Tracking")}
            >
              <ListItemIcon sx={styles.itemIcon}>
                <I3DCubeScan variant={selectedRoute === "Tracking" ? "Bold" : "Outline"} style={selectedRoute === "Tracking" ? styles.selectedItemIcon : styles.unselectedItemIcon}/>
              </ListItemIcon>
              <ListItemText primary="Rastreo" />
            </ListItemButton>)}

            {hasPermission(modulePermissions.Lots) && (<ListItemButton
              selected={selectedRoute === "Lots"}
              component={Link}
              to={ROUTES_CONSTANTS.LOTS}
              style={selectedRoute === "Lots" ? styles.selectedItem : styles.unselectedItem}
              onClick={() => handleClickDrawerItem("Lots")}
            >
              <ListItemIcon sx={styles.itemIcon}>
                <Box1 variant={selectedRoute === "Lots" ? "Bold" : "Outline"} style={selectedRoute === "Lots" ? styles.selectedItemIcon : styles.unselectedItemIcon}/>
              </ListItemIcon>
              <ListItemText primary="Recolección" />
            </ListItemButton>)}

            {hasPermission(modulePermissions.Guides) && (<ListItemButton
              selected={selectedRoute === "Guides"}
              component={Link}
              style={selectedRoute === "Guides" ? styles.selectedItem : styles.unselectedItem}
              to={ROUTES_CONSTANTS.GUIDES}
              onClick={() => handleClickDrawerItem("Guides")}
            >
              <ListItemIcon sx={styles.itemIcon}>
                <Document variant={selectedRoute === "Guides" ? "Bold" : "Outline"} style={selectedRoute === "Guides" ? styles.selectedItemIcon : styles.unselectedItemIcon}/>
              </ListItemIcon>
              <ListItemText primary="Rutas" />
            </ListItemButton>)}

            {hasDeliveryGuidesPermission && (<ListItemButton
              selected={selectedRoute === "DeliveryGuides"}
              component={Link}
              style={selectedRoute === "DeliveryGuides" ? styles.selectedItem : styles.unselectedItem}
              to={ROUTES_CONSTANTS.DELIVERY_GUIDES}
              onClick={() => handleClickDrawerItem("DeliveryGuides")}
            >
              <ListItemIcon sx={styles.itemIcon}>
                <DocumentForward variant={selectedRoute === "DeliveryGuides" ? "Bold" : "Outline"} style={selectedRoute === "DeliveryGuides" ? styles.selectedItemIcon : styles.unselectedItemIcon}/>
              </ListItemIcon>
              <ListItemText primary="Guias por entregar" />
            </ListItemButton>)}
          </List>
        </Collapse>)}

        {hasPermission(modulePermissions.Deliverers) && (<ListItemButton
          selected={selectedRoute === "Deliverers"}
          component={Link}
          style={selectedRoute === "Deliverers" ? styles.selectedItem : styles.unselectedItem}
          to={ROUTES_CONSTANTS.DELIVERERS}
          onClick={() => handleClickDrawerItem("Deliverers")}
        >
          <ListItemIcon sx={styles.itemIcon}>
            <Moneys variant={selectedRoute === "Deliverers" ? "Bold" : "Outline"} style={selectedRoute === "Deliverers" ? styles.selectedItemIcon : styles.unselectedItemIcon}/>
          </ListItemIcon>
          <ListItemText primary="Corte" />
        </ListItemButton>)}
        
        {hasCategoriesPermission && (<ListItemButton
          selected={selectedRoute === "Categories"}
          component={Link}
          style={selectedRoute === "Categories" ? styles.selectedItem : styles.unselectedItem}
          to={ROUTES_CONSTANTS.CATEGORIES}
          onClick={() => handleClickDrawerItem("Categories")}
        >
          <ListItemIcon sx={styles.itemIcon}>
            <Category variant={selectedRoute === "Categories" ? "Bold" : "Outline"} style={selectedRoute === "Categories" ? styles.selectedItemIcon : styles.unselectedItemIcon}/>
          </ListItemIcon>
          <ListItemText primary="Categorías" />
        </ListItemButton>)}
        
        
        {hasTicketsPermission && (<ListItemButton
          selected={selectedRoute === "Tickets"}
          component={Link}
          style={selectedRoute === "Tickets" ? styles.selectedItem : styles.unselectedItem}
          to={ROUTES_CONSTANTS.TICKETS}
          onClick={() => handleClickDrawerItem("Tickets")}
        >
          <ListItemIcon sx={styles.itemIcon}>
            <Receipt21 variant={selectedRoute === "Tickets" ? "Bold" : "Outline"} style={selectedRoute === "Tickets" ? styles.selectedItemIcon : styles.unselectedItemIcon}/>
          </ListItemIcon>
          <ListItemText primary="Tickets" />
        </ListItemButton> )}
        
        {hasPermission(modulePermissions.Report) && (<ListItemButton
          selected={selectedRoute === "Report"}
          component={Link}
          style={selectedRoute === "Report" ? styles.selectedItem : styles.unselectedItem}
          to={ROUTES_CONSTANTS.REPORT}
          onClick={() => handleClickDrawerItem("Report")}
        >
          <ListItemIcon sx={styles.itemIcon}>
            <ClipboardText variant={selectedRoute === "Report" ? "Bold" : "Outline"} style={selectedRoute === "Report" ? styles.selectedItemIcon : styles.unselectedItemIcon}/>
          </ListItemIcon>
          <ListItemText primary="Reporte" />
        </ListItemButton> )}
      </List>
    </Container>
  );

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? isMobileOpen : true}
      onClose={handleCloseDrawer}
      ModalProps={{ keepMounted: true }}
      sx={{ "& .MuiDrawer-paper": { boxSizing: "border-box", width: 280 } }}
    >
      {drawer}
    </Drawer>
  );
};

export default NavigationDrawer;